import React, { useEffect, useRef, useState } from "react";
import { saveAffFromURL } from "../utils/aff";

/* ===== セクション種別 ===== */
type Section =
  | { kind: "image"; src: string; id?: string; mt?: number; mb?: number }
  | { kind: "video"; src: string; id?: string; mt?: number; mb?: number }
  | { kind: "text"; id?: string; text: string; mt?: number; mb?: number }
  | {
      kind: "banner";
      id: "line-banner-1" | "line-banner-2";
      mt?: number;
      mb?: number;
    };

/* ===== 定数 ===== */
const base = "/assets/member/";
const VIDEO_SRC = `${base}okuman-pv01.mp4`;
const BAND_TEXT = "\\ 登録無料！今すぐAI予想情報をチェック /";

/* ENV優先。無ければ /api/auth/line へ */
const LINE_URL: string =
  (import.meta as any)?.env?.VITE_LINE_REDIRECT_URL ||
  (process.env as any)?.REACT_APP_LINE_REDIRECT_URL ||
  "/api/auth/line";

/* ===== 並び順 ===== */
const SECTIONS: Section[] = [
  { kind: "image", src: `${base}EntryLP01.webp`, id: "hero", mt: 8, mb: 12 },
  { kind: "text", id: "lead-1", text: BAND_TEXT, mb: 10 },

  // ← 上部ボタン
  { kind: "banner", id: "line-banner-1", mb: 16 },

  { kind: "video", src: VIDEO_SRC, id: "pv", mb: 16 },
  { kind: "image", src: `${base}EntryLP03.webp`, mb: 12 },
  { kind: "image", src: `${base}EntryLP04.webp`, mb: 12 },
  { kind: "image", src: `${base}EntryLP06.webp`, mb: 12 },
  { kind: "image", src: `${base}EntryLP07.webp`, mb: 12 },
  { kind: "image", src: `${base}EntryLP08.webp`, mb: 8 },
  { kind: "image", src: `${base}EntryLP09.webp`, mb: 12 },
  { kind: "image", src: `${base}EntryLP10.webp`, mb: 12 },
  { kind: "image", src: `${base}EntryLP11.webp`, mb: 16 },
  { kind: "text", id: "lead-2", text: BAND_TEXT, mb: 12 },

  // ← 最下部ボタン
  { kind: "banner", id: "line-banner-2", mb: 40 },
];

/* ===== 見た目 ===== */
const stackStyle: React.CSSProperties = {
  maxWidth: 720,
  width: "100%",
  margin: "0 auto",
  padding: "16px 0 100px",
};
const gap = (mt?: number, mb?: number): React.CSSProperties => ({
  ...(mt ? { marginTop: mt } : {}),
  ...(mb ? { marginBottom: mb } : {}),
});

/* ===== 追加コンポーネント ===== */
function InlineVideo({
  src,
  style,
}: {
  src: string;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = async () => {
      try {
        await v.play();
      } catch {}
    };
    tryPlay();
  }, []);

  const unmute = async () => {
    const v = ref.current;
    if (!v) return;
    try {
      v.muted = false;
      setMuted(false);
      await v.play();
    } catch {
      v.controls = true;
    }
  };

  return (
    <div className="relative" style={{ position: "relative" }}>
      <video
        ref={ref}
        src={src}
        style={{ ...style, display: "block" }}
        playsInline
        muted={muted}
        autoPlay
        loop
        preload="metadata"
      />
      {muted && (
        <button
          onClick={unmute}
          className="absolute"
          aria-label="音声をオンにする"
          style={{
            position: "absolute",
            right: 16,
            bottom: 16,
            padding: "10px 14px",
            borderRadius: 12,
            background: "rgba(0,0,0,0.55)",
            color: "#fff",
            fontWeight: 700,
            border: "1px solid rgba(255,255,255,0.2)",
            backdropFilter: "blur(4px)",
          }}
        >
          🔊 音声オン
        </button>
      )}
    </div>
  );
}

/* ===== ページ ===== */
export default function EntryLP() {
  useEffect(() => {
    // ① URL の bom / aff を localStorage に保存
    saveAffFromURL();

    // ★② bom をサーバー(session/cookie)にも保存する（最重要）
    const params = new URLSearchParams(window.location.search);
    const bom = params.get("bom");
    if (bom) {
      fetch("/api/public/prefix-aff", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ bom }),
      }).catch(() => {});
    }
  }, []);



  // ログインボタンクリック時に affCode を付けて LINE_URL へ飛ばす
  const handleLogin = (
    e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => {
    if (e) e.preventDefault();

    const code = localStorage.getItem("affCode");
    let url = LINE_URL;

    if (code) {
      const sep = url.includes("?") ? "&" : "?";
      url = `${url}${sep}bom=${encodeURIComponent(code)}`;
    }

    window.location.href = url;
  };

  return (
    <div className="lp">
      <div className="stack" style={stackStyle}>
        {SECTIONS.map((s, i) => {
          if (s.kind === "image") {
            return (
              <div id={s.id} style={gap(s.mt, s.mb)} key={`img-${i}`}>
                <img
                  src={s.src}
                  alt={s.id || ""}
                  style={{ width: "100%", height: "auto", display: "block" }}
                  loading="lazy"
                />
              </div>
            );
          }
          if (s.kind === "video") {
            return (
              <div id={s.id} style={gap(s.mt, s.mb)} key={`vid-${i}`}>
                <InlineVideo
                  src={s.src}
                  style={{ width: "100%", borderRadius: 8 }}
                />
              </div>
            );
          }
          if (s.kind === "text") {
            return (
              <div id={s.id} style={gap(s.mt, s.mb)} key={`txt-${i}`}>
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: 900,
                    fontSize: 22,
                    color: "#0033cc",
                    letterSpacing: 1,
                    lineHeight: 1.4,
                    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                  }}
                >
                  {s.text}
                </div>
              </div>
            );
          }

          // banner（画像リンク）
          return (
            <div id={s.id} style={gap(s.mt, s.mb)} key={`ban-${i}`}>
              <a
                href={LINE_URL}
                aria-label="LINEログインへ"
                style={{ display: "block" }}
                onClick={handleLogin}
              >
                <img
                  src={`${base}EntryLP05.webp`}
                  alt={s.id}
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    borderRadius: 12,
                  }}
                  loading="lazy"
                />
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
}
